import * as React from "react";
import {
  Datagrid,
  TextField,
  ReferenceField,
  FunctionField,
  ShowButton,
  NumberField,
  Filter,
  TextInput,
  DateTimeInput,
  useGetList,
  AutocompleteInput,
  SelectInput,
  useRecordContext,
  useNotify,
  useTheme,
} from "react-admin";
import { JsonField } from "react-admin-json-view";
import {
  DateField,
  CustomList,
  CreateWithExportOnlyTopToolbar,
  ExportOnlyTopToolbar,
  EmptyList,
} from "../../shared/components/index.js";
import { TRUNC_STATUSES } from "@local/constants/index.js";

const TruncFilter = (props) => {
  const { data: mpgs } = useGetList("MerchantPaymentGateway");

  return (
    <Filter {...props}>
      {mpgs && (
        <AutocompleteInput
          source="merchant_payment_gateway_id"
          choices={mpgs}
          inputText={(record) => record.name}
          optionText="name"
          optionValue="id"
          debounce={10}
          filterToQuery={(searchText) => ({ name: searchText })}
          alwaysOn
          resettable="true"
        />
      )}
      <TextInput source="client_phone" alwaysOn resettable />
      <SelectInput
        alwaysOn
        resettable
        source="status"
        choices={Object.values(TRUNC_STATUSES).map((status) => ({
          id: status.id,
          name: status.name,
        }))}
        optionText="name"
        optionValue="name"
      />
      <DateTimeInput source="start_date" alwaysOn />
      <DateTimeInput source="end_date" alwaysOn />
    </Filter>
  );
};

const CouponFuelType = () => {
  const record = useRecordContext();

  if (record && record.payload?.coupons?.length) {
    const coupon_id = record.payload?.coupons?.[0]?.id;

    const transformedRecord = {
      ...record,
      coupon_id: coupon_id,
    };

    return (
      <ReferenceField
        source="coupon_id"
        reference="Coupon"
        record={transformedRecord}
        link={false}
      >
        <TextField source="fuel_type" />
      </ReferenceField>
    );
  }
};

const CouponTotalNominal = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [coupons, setCoupons] = React.useState([]);
  const { data, error } = useGetList("Coupon", {
    filter: { ids: record?.payload?.coupons.map(({ id }) => id) },
  });

  React.useEffect(() => {
    if (data && data.length) {
      setCoupons(data);
    }
  }, [data]);

  if (error) {
    return notify(`resources.notifications.errors.${error?.message}`, {
      type: "error",
    });
  }

  if (coupons && coupons.length) {
    const totalNominal = coupons.reduce(
      (sum, coupon) => sum + (coupon.nominal || 0),
      0
    );

    const transformedRecord = {
      ...record,
      amount_nominal: totalNominal,
    };

    return (
      <FunctionField
        record={transformedRecord}
        source="amount_nominal"
        render={(record) => record && record.amount_nominal}
      />
    );
  }
};

const listTruncs = (props) => {
  const { isCreateAllowed } = props;
  const plugins = JSON.parse(localStorage.getItem("plugins"));
  const theme = useTheme();

  return (
    <CustomList
      empty={<EmptyList resource="Trunc" isCreateAllowed={isCreateAllowed} />}
      actions={
        isCreateAllowed === true ? (
          <CreateWithExportOnlyTopToolbar />
        ) : (
          <ExportOnlyTopToolbar />
        )
      }
      filters={<TruncFilter {...props} />}
    >
      <Datagrid
        empty={<EmptyList resource="Trunc" isCreateAllowed={isCreateAllowed} />}
        rowClick={false}
        bulkActionButtons={false}
      >
        <ReferenceField
          source="merchant_payment_gateway_id"
          reference="MerchantPaymentGateway"
          link="show"
        >
          <TextField
            sx={{
              color: theme[0] === "dark" ? "#E6E6FA" : "#1976d2",
            }}
            source="name"
          />
        </ReferenceField>
        <FunctionField
          source="client_phone"
          render={(record) => (
            <a
              className={`custom-link ${
                theme[0] === "dark" ? "dark" : "light"
              }`}
              href={`/#/CLient/${record.client_id}/show`}
            >
              {record.client_phone}
            </a>
          )}
        />

        <ReferenceField
          source="client_id"
          reference="Client"
          link="show"
          label="resources.Trunc.fields.datex_id"
        >
          <TextField
            sx={{
              color: theme[0] === "dark" ? "#E6E6FA" : "#1976d2",
            }}
            source="id"
          />
        </ReferenceField>
        <TextField source="description" />
        {plugins && plugins.datexApiPayment && (
          <TextField
            sortable={false}
            source="session_id"
            // sx={{
            //   color: theme[0] === "dark" ? "#E6E6FA" : "#1976d2",
            // }}
          />
        )}
        <NumberField source="order_id" textAlign="center" />
        {plugins && plugins.datexCoupon && (
          <CouponFuelType label="resources.Trunc.fields.coupon_id" />
        )}
        {plugins && plugins.datexCoupon && (
          <CouponTotalNominal label="resources.Trunc.fields.amount_nominal" />
        )}
        <FunctionField
          source="amount"
          render={(record) =>
            record.amount ? record.amount : record.amount_alt
          }
        />
        <JsonField
          source="transactions"
          reactJsonOptions={{
            name: null,
            collapsed: true,
            enableClipboard: false,
            displayDataTypes: false,
          }}
          sortable={false}
        />
        <JsonField
          source="payload"
          reactJsonOptions={{
            name: null,
            collapsed: true,
            enableClipboard: false,
            displayDataTypes: false,
          }}
          sortable={false}
        />
        <FunctionField source="status" render={(record) => record.status} />
        <DateField source="created_at" />
        <DateField source="updated_at" />
        <ShowButton className="button-show" />
      </Datagrid>
    </CustomList>
  );
};

export default listTruncs;
